/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Code Explorer constants
 */
interface ICodeSetSystemOption {
	key: CodeExplorer.ICodeSetSystem;
	label: string;
	value: CodeExplorer.ICodeSetSystem;
}

const CODE_SET_MAPPING: Record<
	'ICD10CM' | 'ICD10PCS' | 'HCPCS' | 'HCPCS_MODIFIER' | 'CPT' | 'CPT_MODIFIER' | 'ICD9DX' | 'ICD9SG',
	CodeExplorer.ICodeSetSystem
> = {
	ICD10CM: 'ICD-10-CM',
	ICD10PCS: 'ICD-10-PCS',
	CPT: 'CPT',
	CPT_MODIFIER: 'CPT-MODIFIER',
	HCPCS: 'HCPCS',
	HCPCS_MODIFIER: 'HCPCS-MODIFIER',
	ICD9DX: 'ICD-9-DX',
	ICD9SG: 'ICD-9-SG',
};

const CODE_SET_SYSTEM_OPTIONS: ICodeSetSystemOption[] = [
	{
		key: CODE_SET_MAPPING.ICD10CM,
		label: 'ICD-10-CM',
		value: CODE_SET_MAPPING.ICD10CM,
	},
	{
		key: CODE_SET_MAPPING.ICD10PCS,
		label: 'ICD-10-PCS',
		value: CODE_SET_MAPPING.ICD10PCS,
	},
	{
		key: CODE_SET_MAPPING.CPT,
		label: 'CPT',
		value: CODE_SET_MAPPING.CPT,
	},
	{
		key: CODE_SET_MAPPING.CPT_MODIFIER,
		label: 'CPT Modifier',
		value: CODE_SET_MAPPING.CPT_MODIFIER,
	},
	{
		key: CODE_SET_MAPPING.HCPCS,
		label: 'HCPCS',
		value: CODE_SET_MAPPING.HCPCS,
	},
	{
		key: CODE_SET_MAPPING.HCPCS_MODIFIER,
		label: 'HCPCS Modifier',
		value: CODE_SET_MAPPING.HCPCS_MODIFIER,
	},
	{
		key: CODE_SET_MAPPING.ICD9DX,
		label: 'ICD-9-DX',
		value: CODE_SET_MAPPING.ICD9DX,
	},
	{
		key: CODE_SET_MAPPING.ICD9SG,
		label: 'ICD-9-SG',
		value: CODE_SET_MAPPING.ICD9SG,
	},
];

const CODE_STATUS_MAPPING: Record<'active' | 'terminated' | 'futureUse', CodeExplorer.ICodeStatus> = {
	active: 'ActiveCode',
	terminated: 'TerminatedCode',
	futureUse: 'FutureUseCode',
};

const CPT_PLA_CATEGORY: CodeExplorer.ICPTCategory = 'Proprietary Laboratory Analyses ( PLA )';

const DEFAULT_SELECTED_CODE_SET_SYSTEM = [
	CODE_SET_MAPPING.ICD10CM,
	CODE_SET_MAPPING.ICD10PCS,
	CODE_SET_MAPPING.CPT,
	CODE_SET_MAPPING.CPT_MODIFIER,
	CODE_SET_MAPPING.HCPCS,
	CODE_SET_MAPPING.HCPCS_MODIFIER,
] as CodeExplorer.ICodeSetSystem[];
const SHOW_HIERARCHY_CODES = [CODE_SET_MAPPING.ICD10CM];

const NO_DATA_PLACEHOLDER = '-';
const POSTER_URL =
	'https://res.cloudinary.com/abegerho/image/upload/v1751531512/veritable-website/Medical_Code_Lookup_With_Veritable_Code_Explorer_9d47c2eede.png';

const POSTER_URL_WITH_PLAY_ICON =
	'https://res.cloudinary.com/abegerho/image/upload/v1751466073/veritable-website/Code_Lookup_Code_Explorer_Veritable_32856e38a2.png';

const REACTION_KEYS = {
	favourite: 'favourite',
};

const TAB_ITEMS = {
	codeDescription: {
		key: 'description',
		label: 'Code Description',
	},
	codeHierarchy: {
		key: 'hierarchy',
		label: 'Code Hierarchy',
	},
	internalNotes: {
		key: 'notes',
		label: 'Internal Note',
	},
};

const DETAILED_CODE_VIEW_FIELDS: Record<string, CodeExplorer.ICodeExplorerFields> = {
	HCPCS: {
		// array
		crossReferenceCode: {
			key: 'crossreferencecode',
			label: 'Cross Reference Code',
			description: `An explicit reference crosswalking a deleted code or a code that is not valid for Medicare to a valid current code (or range of codes).`,
		},
		// array
		pricingIndicator: {
			key: 'pricingindicator',
			label: 'Pricing Indicator Code',
			description: `Code used to identify the appropriate methodology for developing unique pricing amounts under part B. A procedure may have one to four pricing codes.`,
		},
		multiplePricingIndicator: {
			key: 'multiplepricingindicator',
			label: 'Multiple Pricing Indicator Code',
			description: `Code used to identify instances where a procedure could be priced under multiple methodologies.`,
		},
		// array
		cimReferenceSectionNumber: {
			key: 'coverageissuesmanual',
			label: 'CIM Reference Section Number',
			description: `Number identifying the reference section of the coverage issues manual.`,
		},
		// array
		mcmReferenceSection: {
			key: 'medicarecarriersmanual',
			label: 'MCM Reference Section Number',
			description: `Number identifying a section of the Medicare carriers manual.`,
		},
		statuteNumber: {
			key: 'statutenumber',
			label: 'Statute Number',
			description: `Number identifying statute reference for coverage or noncoverage of procedure or service.`,
		},
		// array
		labCertification: {
			key: 'labcertification',
			label: 'Lab Certification Code',
			description: `Code used to classify laboratory procedures according to the specialty certification categories listed by CMS. Any generally certified laboratory (e.g., 100) may perform any of the tests in its subgroups (e.g., 110, 120, etc.).`,
		},
		coverageCode: {
			key: 'coverage',
			label: 'Coverage Code',
			description: `A code denoting Medicare coverage status.`,
		},
		ascPaymentGroup: {
			key: 'ascpaymentgroupcode',
			label: 'ASC Payment Group',
			description: `The 'YY' indicator represents that this procedure is approved to be performed in an ambulatory surgical center. You must access the ASC tables on the mainframe or CMS website to get the dollar amounts.`,
		},
		ascPaymentGroupEffectiveDate: {
			key: 'ascpaymentgroupeffectivedate',
			label: 'ASC Payment Group Effective Date',
			description: `The date the procedure is assigned to the ASC payment group. (YYYYMMDD)`,
		},
		mogPaymentGroupCode: {
			key: 'mogpaymentgroupcode',
			label: 'MOG Payment Group Code',
			description: `Medicare outpatient groups (MOG) payment group code`,
		},
		processingNotesNumber: {
			key: 'processingnotes',
			label: 'Processing Note Number',
			description: `Number identifying the processing note contained in Appendix A of the HCPCS manual.`,
		},
		mogPaymentPolicyIndicator: {
			key: 'mogpaymentpolicyindicator',
			label: 'MOG Payment Policy Indicator',
			description: `Indicator identifying whether a HCPCS code is subject to payment of an ASC facility fee, to a separate fee under another provision of Medicare, or to no fee at all.`,
		},
		berensonEggersTypeOfServiceCode: {
			key: 'berensoneggerstypeofservice',
			label: 'Berenson-Eggers Type of Service Code',
			description: `This field is valid beginning with 2003 data. The Berenson-Eggers Type of Service (BETOS) for the procedure code based on generally agreed upon clinically meaningful groupings of procedures and services.`,
		},
		// array
		typeOfServiceCode: {
			key: 'typeofservice',
			label: 'Type of Service Code',
			description: `The carrier assigned CMS type of service which describes the particular kind(s) of service represented by the procedure code.`,
		},
		baseUnitQuantity: {
			key: 'anesthesiabaseunitquantity',
			label: 'Base Unit Quantity',
			description: `The base unit represents the level of intensity for anesthesia procedure services that reflects all activities except time. These activities include usual preoperative and post-operative visits, the administration of fluids and/or blood incident to anesthesia care, and monitering procedures. (Note: the payment amount for anesthesia services is based on a calculation using base unit, time units, and the conversion factor.)`,
		},
		codeAddedDate: {
			key: 'addeddate',
			label: 'Code Added Date',
			description: `The year the HCPCS code was added to the Healthcare common procedure coding system.`,
		},
		actionEffectiveDate: {
			key: 'effectivedate',
			label: 'Action Effective Date',
			description: `Effective date of action to a procedure or modifier code`,
		},
		terminationDate: {
			key: 'terminationdate',
			label: 'Termination Date',
			description: `Last date for which a procedure or modifier code may be used by Medicare providers.`,
		},
		actionCode: {
			key: 'actioncode',
			label: 'Action Code',
			description: `A code denoting the change made to a procedure or modifier code within the HCPCS system.`,
		},
	},
	ICD10CM: {
		codeGuidelines: {
			key: 'codeguidelines',
			label: 'Code Guidelines',
			description: `Code-level rules and sequencing instructions that apply to this block of codes.`,
		},
		sectionGuidelines: {
			key: 'sectionguidelines',
			label: 'Section Guidelines/Section Notes',
			description: `Section-level rules and sequencing instructions that apply to this block of codes.`,
		},
		chapterGuidelines: {
			key: 'chapterguidelines',
			label: 'Chapter Guidelines/Chapter Notes',
			description: `Chapter-level rules and sequencing instructions that apply to this block of codes.`,
		},
		// these fields will be rendered in future
		// clinicalCondition: {
		// 	key: 'clinicalcondition',
		// 	label: 'Clinical Condition',
		// 	description: `Brief clinical description of the disorder or injury represented by the code.`,
		// },
		// layTerm: {
		// 	key: 'layterm',
		// 	label: 'Lay Term',
		// 	description: `Plain-language explanation of the diagnosis for non-clinical readers.`,
		// },
	},
	CPT: {
		category: {
			key: 'category',
			label: 'Category',
			description: ``,
		},
		section: {
			key: 'section',
			label: 'Section',
			description: ``,
		},
		description: {
			key: 'description',
			label: 'Description',
			description: ``,
		},
		consumer: {
			key: 'consumer',
			label: 'Consumer',
			description: `Translated code descriptor from the official CPT code set into language that is easily understood by the average patient and/or his or her caregiver. **Category II codes are excluded.`,
		},
		currentDescriptorEffectiveDate: {
			key: 'currentdescriptoreffectivedate',
			label: 'Current Descriptor Effective Date',
			description: `Current Descriptor Effective Date starts with the 2021 Code set. If the code has been added or revised since 2021, this field will display the date the current descriptor became effective.`,
		},
		// next 3 only for PLA
		testName: {
			key: 'testname',
			label: 'Test Name',
			description: `Name of Laboratory test.`,
		},
		labName: {
			key: 'labname',
			label: 'Lab Name',
			description: `Name of Laboratory.`,
		},
		manufacturerName: {
			key: 'manufacturername',
			label: 'Manufacturer Name',
			description: `Name of Manufacturer for test.`,
		},
		// next 2 will only be rendered for cpt modifier
		modifierLevel: {
			key: 'modifierlevel',
			label: 'Modifier Level',
			description: ``,
		},
		modifierDescriptorEffectiveDate: {
			key: 'modifierdescriptoreffectivedate',
			label: 'Modifier Descriptor Effective Date',
			description: `Modifier Descriptor Effective Date starts with the 2021 Code set. If the code has been added or revised since 2021, this field will display the date the current descriptor became effective.`,
		},
		guideLines: {
			key: 'guidelines',
			label: 'Official AMA Guidelines',
			description: `Official AMA Guidelines`,
		},
	},
};

const PLA_KEYS = [
	DETAILED_CODE_VIEW_FIELDS.CPT.testName.key,
	DETAILED_CODE_VIEW_FIELDS.CPT.labName.key,
	DETAILED_CODE_VIEW_FIELDS.CPT.manufacturerName.key,
];
const MODIFIER_KEYS = [DETAILED_CODE_VIEW_FIELDS.CPT.modifierLevel.key, DETAILED_CODE_VIEW_FIELDS.CPT.modifierDescriptorEffectiveDate.key];

const ICD10CM_GUIDELINE_SUB_FIELDS = {
	[DETAILED_CODE_VIEW_FIELDS.ICD10CM.codeGuidelines.key]: [
		'Description',
		'Notes',
		'Inclusion Term',
		'Includes',
		'Use Additional Code',
		'Code First',
		'Code Also',
		'Excludes 1',
		'Excludes 2',
		'Seventh Character Note',
	],
	[DETAILED_CODE_VIEW_FIELDS.ICD10CM.sectionGuidelines.key]: [
		'Description',
		'Notes',
		'Inclusion Term',
		'Includes',
		'Use Additional Code',
		'Code First',
		'Code Also',
		'Excludes 1',
		'Excludes 2',
	],
	[DETAILED_CODE_VIEW_FIELDS.ICD10CM.chapterGuidelines.key]: [
		'Description',
		'Notes',
		'Includes',
		'Use Additional Code',
		'Code First',
		'Code Also',
		'Excludes 1',
		'Excludes 2',
	],
};

const CPT_CODE_CATEGORY = {
	'Category I': 'CATEGORY_I',
	'Category II': 'CATEGORY_II',
	'Category III': 'CATEGORY_III',
	'Proprietary Laboratory Analyses ( PLA )': 'PLA',
	'Administrative Multianalyte Assays with Algorithmic Analyses ( Administrative MAAA )': 'ADMINISTRATIVE_MAAA',
};

const CPT_CODE_SECTION = {
	'Evaluation and Management': 'EVALUATION',
	Anesthesiology: 'ANESTHESIOLOGY',
	Surgery: 'SURGERY',
	'Radiology (Including Nuclear Medicine and Diagnostic Ultrasound)': 'RADIOLOGY',
	'Pathology and Laboratory': 'PATHOLOGY',
	Medicine: 'MEDICINE',
};

const CPT_GUIDELINES = {
	CPT_CATEGORY_I_EVALUATION: 'EvalManagementGuidelines',
	CPT_CATEGORY_I_ANESTHESIOLOGY: 'AnesthesiaGuidelines',
	CPT_CATEGORY_I_SURGERY: 'SurgeryGuidelines',
	CPT_CATEGORY_I_RADIOLOGY: 'RadiologyGuidelines',
	CPT_CATEGORY_I_PATHOLOGY: 'PathLabGuidelines',
	CPT_CATEGORY_I_MEDICINE: 'MedicineGuidelines',
	CPT_CATEGORY_II: 'CatIIGuidelines',
	CPT_CATEGORY_III: 'CatIIIGuidelines',
};

export {
	CODE_SET_SYSTEM_OPTIONS,
	DETAILED_CODE_VIEW_FIELDS,
	SHOW_HIERARCHY_CODES,
	REACTION_KEYS,
	DEFAULT_SELECTED_CODE_SET_SYSTEM,
	TAB_ITEMS,
	NO_DATA_PLACEHOLDER,
	CODE_SET_MAPPING,
	CODE_STATUS_MAPPING,
	PLA_KEYS,
	MODIFIER_KEYS,
	CPT_PLA_CATEGORY,
	POSTER_URL,
	POSTER_URL_WITH_PLAY_ICON,
	ICD10CM_GUIDELINE_SUB_FIELDS,
	CPT_CODE_CATEGORY,
	CPT_CODE_SECTION,
	CPT_GUIDELINES,
};
